import type { LocaleCode } from '@kidova/i18n';

/** English first, Arabic first-class, German available (ADR 0003); tr stays a stub and is not offered. */
export const APP_LOCALES = ['en', 'ar', 'de'] as const satisfies readonly LocaleCode[];
export type AppLocale = (typeof APP_LOCALES)[number];
export type ThemeChoice = 'light' | 'dark' | 'system';

const THEME_KEY = 'kd-theme';
const LOCALE_KEY = 'kd-locale';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only.
  }
}

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value === 'de' || value === 'en' || value === 'ar';
}

/** What boot.js already put on <html> before the first paint. */
export function currentLocale(): AppLocale {
  const lang = document.documentElement.getAttribute('lang');
  return isAppLocale(lang) ? lang : 'en';
}

export function themeChoice(): ThemeChoice {
  const stored = read(THEME_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

export function resolvedTheme(choice: ThemeChoice = themeChoice()): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice): void {
  write(THEME_KEY, choice === 'system' ? null : choice);
  const theme = resolvedTheme(choice);
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

export function applyLocale(locale: AppLocale): void {
  write(LOCALE_KEY, locale);
  // Caddy picks the prerendered public pages in this language from the cookie (Caddyfile).
  document.cookie = `${LOCALE_KEY}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax; Secure`;
  document.documentElement.setAttribute('lang', locale);
  document.documentElement.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
}

/** Sign-in hint only (never a credential): lets a reload go straight to the shell or to /login. */
const AUTH_HINT = 'kd-signed-in';
export const authHint = {
  get: () => read(AUTH_HINT) === '1',
  set: (on: boolean) => {
    write(AUTH_HINT, on ? '1' : null);
  },
};
